import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass } from 'lucide-react';
import advisor from '../../lib/advisor';

/**
 * AdvisorNavLink — link "Mi decisión" siempre visible en el header cuando
 * existe un Context activo (registrado o anónimo).
 *
 * Rompe el cuello de botella detectado: antes el link vivía dentro del
 * dropdown del avatar (solo usuarios logueados). Ahora, si el usuario
 * inició cualquier recorrido — búsqueda, vista de propiedad — el acceso
 * queda en la barra principal.
 *
 * Muestra un chip pequeño con la readiness cuando está disponible.
 */
const STAGE_LABEL = {
  exploring: 'Explorando',
  focusing: 'Enfocando',
  comparing: 'Comparando',
  ready: 'Listo',
};

export default function AdvisorNavLink({ className = '', onClick, mobile = false }) {
  const [hasContext, setHasContext] = useState(false);
  const [stage, setStage] = useState(null);

  useEffect(() => {
    // Detección inicial (barata) — solo mira sessionStorage
    const ctxId = advisor.getActiveContextId();
    if (ctxId) setHasContext(true);

    let cancelled = false;

    // Enriquecemos con readiness solo si hay Context — sin bloquear render.
    const loadStage = async () => {
      if (!advisor.getActiveContextId()) return;
      const q = await advisor.getDecisionQuality({ recordView: false });
      if (!cancelled && q?.readiness) setStage(q.readiness);
    };
    if (ctxId) loadStage();

    // Reactivo: cuando otra página crea/actualiza el Context, refrescamos.
    const onCtxUpdated = (e) => {
      const hasId = !!e?.detail?.id;
      setHasContext(hasId);
      if (hasId) loadStage();
      else setStage(null);
    };
    window.addEventListener('habita:context:updated', onCtxUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener('habita:context:updated', onCtxUpdated);
    };
  }, []);

  if (!hasContext) return null;

  const chip = stage ? (
    <span
      className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-habita-mint text-habita-dark"
      data-testid="advisor-nav-chip"
    >
      {STAGE_LABEL[stage] || stage}
    </span>
  ) : null;

  if (mobile) {
    return (
      <Link
        to="/mi-decision"
        onClick={onClick}
        data-testid="advisor-nav-link-mobile"
        className={`block px-4 py-3 text-sm font-medium text-habita-dark bg-habita-mint/40 hover:bg-habita-mint rounded-lg ${className}`}
      >
        <span className="inline-flex items-center gap-2">
          <Compass className="w-4 h-4 text-habita" /> Mi decisión {chip}
        </span>
      </Link>
    );
  }

  return (
    <Link
      to="/mi-decision"
      onClick={onClick}
      data-testid="advisor-nav-link"
      className={`hidden md:inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-habita/30 bg-white text-habita-dark text-sm font-medium hover:bg-habita-mint transition-colors ${className}`}
    >
      <Compass className="w-4 h-4 text-habita" />
      Mi decisión
      {chip}
    </Link>
  );
}
